import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { CurrentShareholderGeneralMeetingModel } from '../../../models/current-shareholder-general-meeting.model';
import { RegistrationStatusEnum } from '../../../models/enums/registration-status.enum';
import { GeneralMeetingModel } from '../../../models/general-meeting.model';
import { VotingInstructionsModel } from '../../../models/voting-instructions.model';
import { GeneralMeetingCheckInService } from '../../../services/general-meeting-check-in.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { AgendaItemsComponent } from '../../dialogs/agenda-items/agenda-items.component';
import { GateQrCodeComponent } from '../../dialogs/gate-qr-code/gate-qr-code.component';
import { GeneralMeetingSettingsDialog } from '../../dialogs/general-meeting-settings/general-meeting-settings.dialog';

@Component({
    selector: 'sl-general-meeting-settings',
    templateUrl: './general-meeting-settings.component.html',
    styleUrls: ['./general-meeting-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralMeetingSettingsComponent extends BaseComponent implements OnInit {
    RegistrationStatusEnum = RegistrationStatusEnum;

    isLoading = false;
    currentMeeting: GeneralMeetingModel;
    currentShareholderData: CurrentShareholderGeneralMeetingModel;
    registrationStatus = RegistrationStatusEnum.NoAnswer;
    emptyBox = 'check_box_outline_blank';
    checkedBox = 'check_box';

    private readonly matDialog = inject(MatDialog);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly generalMeetingCheckinService = inject(GeneralMeetingCheckInService);

    ngOnInit(): void {
        const isCheckedInSub = this.generalMeetingCheckinService.checkInStatusChange().subscribe(() => {
            this.fetchData();
        });
        this.addSubscriptions(isCheckedInSub);

        this.fetchData();
    }

    showQrCode(): void {
        this.matDialog.open(GateQrCodeComponent, {
            width: '400px',
            height: 'auto',
            maxWidth: '98vw',
            maxHeight: '98svh'
        });
    }

    openEditDialog(): void {
        this.matDialog.open(GeneralMeetingSettingsDialog, {
            width: '600px',
            height: '800px',
            maxWidth: '98vw',
            maxHeight: '98svh'
        });
    }

    onGiveInstructions(): void {
        let isEditing = false;

        if (
            this.registrationStatus === RegistrationStatusEnum.Representative
            || this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative
        ) {
            isEditing = true;
        }

        const ref = this.matDialog.open(AgendaItemsComponent, {
            minWidth: '65%',
            minHeight: '40%',
            data: new VotingInstructionsModel({
                shareholderData: this.currentShareholderData,
                generalMeeting: this.currentMeeting,
                isEditing
            })
        });

        ref.afterClosed().subscribe(() =>
            this.generalMeetingsResource.getCurrentForShareholder().subscribe(r => {
                this.currentShareholderData = r.value;
                this.registrationStatus = r.value.registrationStatus;
            })
        );
    }

    onCheckIn(): void {
        this.generalMeetingCheckinService.checkIn();
    }

    onCheckOut(): void {
        this.generalMeetingCheckinService.checkOut();
    }

    private fetchData(): void {
        this.isLoading = true;
        forkJoin({
            meeting: this.generalMeetingsResource.getCurrent(),
            shareholderData: this.generalMeetingsResource.getCurrentForShareholder()
        }).subscribe(r => {
            this.currentMeeting = r.meeting.value;
            this.currentShareholderData = r.shareholderData.value;
            this.isLoading = false;
        });
    }
}
